/* eslint-disable */
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from "@material-ui/core";

import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullScreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ViewModule as FitViewIcon,
} from "@material-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    addEdge,
    useReactFlow,
    ReactFlowProvider,
    BaseEdge,
    getSmoothStepPath,
    getStraightPath,
    Handle,
  } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import AuthContext from "contexts/AuthProvider.js";
import { client } from "../../../../services/axiosClient.js";
import { ThreeDots } from "react-loader-spinner";
import StatusWarningIndicator from "../../../../components/StatusWarningIndicator";

// NOTA: Dagre removido - agora usando posicionamento manual vertical forçado

// Configurações de layout base
const BASE_NODE_WIDTH = 340; // Largura base para resolução premium
const BASE_NODE_HEIGHT = 90; // Altura base para resolução premium
const BASE_FONT_SIZE_WORKFRONT = 1.5; // Fonte base para workfronts
const BASE_FONT_SIZE_INITIATIVE = 1.3; // Fonte base para initiatives
const BASE_FONT_SIZE_BRANCH = 1.6; // Fonte base para branches

// Função para calcular tamanhos proporcionais baseado no número de workfronts
const calculateProportionalSizes = (workfrontCount) => {
  // Fatores de ajuste baseados na quantidade
  let widthFactor, heightFactor, fontFactor, spacingFactor;
  
  if (workfrontCount <= 4) {
    // Poucos workfronts: usar tamanhos grandes para aproveitar espaço
    widthFactor = 1.2;
    heightFactor = 1.15;
    fontFactor = 1.3;
    spacingFactor = 1.4;
  } else if (workfrontCount <= 8) {
    // Quantidade média: tamanhos equilibrados
    widthFactor = 1.0;
    heightFactor = 1.0;
    fontFactor = 1.0;
    spacingFactor = 1.0;
  } else if (workfrontCount <= 12) {
    // Muitos workfronts: tamanhos compactos mas com respiro visual
    widthFactor = 0.8;
    heightFactor = 0.85;
    fontFactor = 0.8;
    spacingFactor = 0.9; // Aumentado de 0.7 para 0.9 para dar mais respiro
  } else {
    // Muitíssimos workfronts: tamanhos mínimos mas com espaçamento mínimo aceitável
    widthFactor = 0.65;
    heightFactor = 0.7;
    fontFactor = 0.65;
    spacingFactor = 0.7; // Aumentado de 0.5 para 0.7 para evitar colagem extrema
  }
  
  // Calcular espaçamentos com mínimo garantido para evitar colagem extrema
  const horizontalSpacing = Math.round((BASE_NODE_WIDTH + 40) * spacingFactor);
  const verticalSpacing = Math.round((BASE_NODE_HEIGHT + 40) * spacingFactor); // Aumentado de 20 para 40 para mais espaço base
  
  // Garantir espaçamento mínimo para evitar colagem
  const minHorizontalSpacing = Math.max(horizontalSpacing, 60); // Mínimo de 60px entre workfronts
  const minVerticalSpacing = Math.max(verticalSpacing, 60); // Aumentado de 30px para 60px entre initiatives
  
  return {
    NODE_WIDTH: Math.round(BASE_NODE_WIDTH * widthFactor),
    NODE_HEIGHT: Math.round(BASE_NODE_HEIGHT * heightFactor),
    FONT_SIZE_WORKFRONT: BASE_FONT_SIZE_WORKFRONT * fontFactor,
    FONT_SIZE_INITIATIVE: BASE_FONT_SIZE_INITIATIVE * fontFactor,
    FONT_SIZE_BRANCH: BASE_FONT_SIZE_BRANCH * fontFactor,
    HORIZONTAL_SPACING: minHorizontalSpacing,
    VERTICAL_SPACING: minVerticalSpacing
  };
};

// Função para calcular posições dos nós com LAYOUT VERTICAL FORÇADO E ALINHAMENTO HORIZONTAL
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  // console.log("[LAYOUT] Iniciando cálculo de layout VERTICAL FORÇADO + ALINHAMENTO HORIZONTAL para", nodes.length, "nós e", edges.length, "arestas");
  
  // Separar nós por tipo
  const workfronts = nodes.filter(n => n.data.hierarchy_type === 'workfront');
  const initiatives = nodes.filter(n => n.data.hierarchy_type === 'initiative');
  
  /*
  console.log("[LAYOUT] Distribuição por tipo:", {
    workfronts: workfronts.length,
    initiatives: initiatives.length
  });
  */
  
  // Calcular tamanhos proporcionais baseado no número de workfronts
  const sizes = calculateProportionalSizes(workfronts.length);
  // console.log("[LAYOUT] Tamanhos proporcionais calculados:", sizes);
  /*
  console.log("[LAYOUT] Espaçamentos finais:", {
    horizontal: sizes.HORIZONTAL_SPACING,
    vertical: sizes.VERTICAL_SPACING,
    workfrontCount: workfronts.length
  });
  */
  
  // PRIMEIRA PASSADA: Posicionar workfronts
  const workfrontPositions = {};
  workfronts.forEach((workfront, index) => {
         workfrontPositions[workfront.id] = {
       x: index * sizes.HORIZONTAL_SPACING, // Espaçamento horizontal proporcional
       y: 0
     };
  });
  
  // SEGUNDA PASSADA: Posicionar initiatives alinhadas com seus workfronts pais
  const initiativePositions = {};
  const workfrontInitiativeCounts = {}; // Contador de initiatives por workfront
  
  initiatives.forEach((initiative) => {
    // Encontrar o workfront pai desta initiative
    const parentWorkfrontEdge = edges.find(e => e.target === initiative.id);
    const parentWorkfrontId = parentWorkfrontEdge?.source;
    
    if (parentWorkfrontId && workfrontPositions[parentWorkfrontId]) {
      // Inicializar contador se não existir
      if (!workfrontInitiativeCounts[parentWorkfrontId]) {
        workfrontInitiativeCounts[parentWorkfrontId] = 0;
      }
      
      // Posicionar initiative abaixo e alinhada com seu workfront pai
      const initiativeIndex = workfrontInitiativeCounts[parentWorkfrontId]++;
      const parentPosition = workfrontPositions[parentWorkfrontId];
      
             initiativePositions[initiative.id] = {
         x: parentPosition.x, // Mesmo X do workfront pai (ALINHAMENTO HORIZONTAL)
         y: (initiativeIndex + 1) * sizes.VERTICAL_SPACING // Espaçamento vertical proporcional
       };
      
      /*
      console.log(`[LAYOUT] Initiative ${initiative.id} (${initiative.data.name}) alinhada com workfront ${parentWorkfrontId}:`, {
        parentX: parentPosition.x,
        initiativeX: initiativePositions[initiative.id].x,
        initiativeY: initiativePositions[initiative.id].y
      });
      */
    } else {
      // console.warn(`[LAYOUT] Initiative ${initiative.id} sem workfront pai encontrado`);
      // Fallback: posicionar no centro
      initiativePositions[initiative.id] = { x: 0, y: 0 };
    }
  });
  
  // TERCEIRA PASSADA: Aplicar posições a todos os nós
  const layoutedNodes = nodes.map((node) => {
    let position;
    
    if (node.data.hierarchy_type === 'workfront') {
      position = workfrontPositions[node.id];
    } else if (node.data.hierarchy_type === 'initiative') {
      position = initiativePositions[node.id];
    } else {
      position = { x: 0, y: 0 };
    }
    
    /*
    console.log(`[LAYOUT] Nó ${node.id} (${node.data.name}):`, {
      type: node.data.hierarchy_type,
      level: node.data.level,
      position: position,
      parentWorkfront: node.data.hierarchy_type === 'initiative' ? 
        edges.find(e => e.target === node.id)?.source : 'N/A'
    });
    */
    
    return {
      ...node,
      targetPosition: 'top',
      sourcePosition: 'bottom',
      position,
    };
  });

  // console.log("[LAYOUT] Layout VERTICAL FORÇADO aplicado");
  
  // DEBUG ESPECÍFICO: Verificar se as posições estão corretas para layout vertical
  const positions = layoutedNodes.map(n => ({ 
    id: n.id, 
    name: n.data.name, 
    x: n.position.x, 
    y: n.position.y,
    type: n.data.hierarchy_type 
  }));
  // console.log("[LAYOUT] Posições finais dos nós:", positions);
  
  // VERIFICAÇÃO CRÍTICA: Verificar se as posições Y estão aumentando (layout vertical)
  const sortedByY = [...positions].sort((a, b) => a.y - b.y);
  /*
  console.log("[LAYOUT] Nós ordenados por posição Y (deve ser crescente para layout vertical):", 
    sortedByY.map(p => `${p.name} (${p.type}): Y=${p.y}`)
  );
  */
  
  // Verificar se há sobreposição horizontal
  const workfrontsPos = positions.filter(p => p.type === 'workfront');
  const initiativesPos = positions.filter(p => p.type === 'initiative');
  
  /*
  console.log("[LAYOUT] Distribuição por tipo:");
  console.log("[LAYOUT] - Workfronts:", workfrontsPos.length, "posições Y:", workfrontsPos.map(w => w.y));
  console.log("[LAYOUT] - Initiatives:", initiativesPos.length, "posições Y:", initiativesPos.map(i => i.y));
  */
  
  // VERIFICAÇÃO FINAL: Garantir que initiatives tenham Y > workfronts
  const maxWorkfrontY = Math.max(...workfrontsPos.map(w => w.y));
  const minInitiativeY = Math.min(...initiativesPos.map(i => i.y));
  
  /*
  console.log("[LAYOUT] Verificação de separação vertical:");
  console.log("[LAYOUT] - Máximo Y dos workfronts:", maxWorkfrontY);
  console.log("[LAYOUT] - Mínimo Y das initiatives:", minInitiativeY);
  console.log("[LAYOUT] - Separados verticalmente:", minInitiativeY > maxWorkfrontY ? "✅ SIM" : "❌ NÃO");
  */
  
  // VERIFICAÇÃO DE ALINHAMENTO HORIZONTAL
  /*
  console.log("[LAYOUT] Verificação de alinhamento horizontal:");
  workfrontsPos.forEach(workfront => {
    const workfrontInitiatives = initiativesPos.filter(initiative => {
      // Encontrar initiative que tem este workfront como pai
      const edge = edges.find(e => e.source === workfront.id && e.target === initiative.id);
      return edge !== undefined;
    });
    
    console.log(`[LAYOUT] Workfront "${workfront.name}" (X=${workfront.x}):`, {
      initiatives: workfrontInitiatives.length,
      positionsX: workfrontInitiatives.map(i => i.x),
      alinhadas: workfrontInitiatives.every(i => Math.abs(i.x - workfront.x) < 10 ? "✅ SIM" : "❌ NÃO")
    });
  });
  */
  
  return { nodes: layoutedNodes, edges };
};

// Componente de nó customizado
function CustomNode({ data, selected, clientColor = "#1e40af", isLoading = false, t, proportionalSizes, onValidationLoadingChange, userDetails }) {
   const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "green":
        return "#10b981";
      case "yellow":
        return "#f59e0b";
      case "red":
        return "#ef4444";
      case "gray":
      default:
        return "#9ca3af";
    }
  };

  const getLevelStyles = (level, hierarchyType, clientColor) => {
    // Usar tamanhos proporcionais se disponíveis, senão usar base
    const sizes = proportionalSizes || {
      FONT_SIZE_WORKFRONT: BASE_FONT_SIZE_WORKFRONT,
      FONT_SIZE_INITIATIVE: BASE_FONT_SIZE_INITIATIVE,
      FONT_SIZE_BRANCH: BASE_FONT_SIZE_BRANCH,
      NODE_WIDTH: BASE_NODE_WIDTH,
      NODE_HEIGHT: BASE_NODE_HEIGHT
    };
    
    // Estilos específicos baseados no tipo de hierarquia
         if (hierarchyType === 'workfront') {
       return {
         backgroundColor: `${clientColor}20`, // ClientColor com 20% de opacidade (mais claro)
         borderColor: "#cbd5e1", // Borda mais escura para destaque
         borderWidth: "2px", // Borda mais grossa
         padding: "18px 26px", // Padding premium para acomodar fontes proporcionais
         fontSize: `${sizes.FONT_SIZE_WORKFRONT}rem`, // Fonte proporcional para workfronts
         fontWeight: "400", // Peso normal (removido bold)
         minHeight: `${sizes.NODE_HEIGHT}px`, // Altura proporcional
         boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)", // Sombra sutil
         borderStyle: "solid",
       };
         } else if (hierarchyType === 'initiative') {
       return {
         backgroundColor: "#ffffff", // Branco para initiatives
         borderColor: "#e2e8f0", // Borda mais suave
         borderWidth: "1px", // Borda fina
         padding: "16px 22px", // Padding premium para acomodar fontes proporcionais
         fontSize: `${sizes.FONT_SIZE_INITIATIVE}rem`, // Fonte proporcional para initiatives
         fontWeight: "400", // Peso normal (removido bold)
         minHeight: `${sizes.NODE_HEIGHT}px`, // Altura proporcional
         boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)", // Sombra mínima
         borderStyle: "solid",
       };
         } else if (hierarchyType === 'branch') {
       return {
         backgroundColor: `${clientColor}40`, // ClientColor com 40% de opacidade (médio)
         borderColor: "#94a3b8", // Borda mais escura
         borderWidth: "2px", // Borda grossa
         padding: "20px 28px", // Padding premium para acomodar fontes proporcionais
         fontSize: `${sizes.FONT_SIZE_BRANCH}rem`, // Fonte proporcional para branches
         fontWeight: "500", // Peso médio (removido muito bold)
         minHeight: `${sizes.NODE_HEIGHT}px`, // Altura proporcional
         boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Sombra mais pronunciada
         borderStyle: "solid",
       };
         } else {
       // Fallback para outros tipos
       return {
         backgroundColor: "#ffffff",
         borderColor: "#e2e8f0",
         borderWidth: "1px",
         padding: "16px 22px",
         fontSize: `${sizes.FONT_SIZE_INITIATIVE}rem`, // Fonte proporcional para fallback
         fontWeight: "400", // Peso normal (removido bold)
         minHeight: `${sizes.NODE_HEIGHT}px`, // Altura proporcional
         boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
         borderStyle: "solid",
       };
     }
  };

     const levelStyles = getLevelStyles(data.level, data.hierarchy_type, clientColor);
   const statusColor = getStatusColor(data.status);
   
   // Usar tamanhos proporcionais se disponíveis, senão usar base
   const sizes = proportionalSizes || {
     FONT_SIZE_WORKFRONT: BASE_FONT_SIZE_WORKFRONT,
     FONT_SIZE_INITIATIVE: BASE_FONT_SIZE_INITIATIVE,
     FONT_SIZE_BRANCH: BASE_FONT_SIZE_BRANCH,
     NODE_WIDTH: BASE_NODE_WIDTH,
     NODE_HEIGHT: BASE_NODE_HEIGHT
   };

      return (
                  <div
          style={{
            width: `${sizes.NODE_WIDTH * 0.995}px`, // Usar largura proporcional
            minHeight: levelStyles.minHeight,
         padding: levelStyles.padding,
         borderRadius: "8px",
         border: `${levelStyles.borderWidth} ${levelStyles.borderStyle} ${levelStyles.borderColor}`,
         backgroundColor: levelStyles.backgroundColor,
         boxShadow: levelStyles.boxShadow,
         position: "relative",
         transition: "all 0.2s ease",
         cursor: data.hierarchy_type === "initiative" ? "pointer" : "default",
         transform: selected ? "scale(1.02)" : "scale(1)",
         display: "flex",
         alignItems: "center",
         justifyContent: "space-between",
         gap: "8px",
                   // Borda lateral removida - status apenas no círculo
       }}
    >
      {/* Handle superior para conexões de entrada - ESSENCIAL para o React Flow */}
      <Handle
        type="target"
        position="top"
        style={{
          background: clientColor,
          width: "8px",
          height: "8px",
          border: "2px solid #ffffff",
        }}
      />
      
      {/* Handle inferior para conexões de saída - ESSENCIAL para o React Flow */}
      <Handle
        type="source"
        position="bottom"
        style={{
          background: clientColor,
          width: "8px",
          height: "8px",
          border: "2px solid #ffffff",
        }}
      />

             {/* Nome do item ou Loading */}
       {isLoading ? (
         <div
           style={{
             display: "flex",
             alignItems: "center",
             justifyContent: "center",
             flex: 1,
             gap: "8px",
           }}
         >
                       <ThreeDots
              color={clientColor}
              height={40}
              width={40}
              ariaLabel="loading"
            />
         </div>
       ) : (
         <div
           style={{
             flex: 1,
             display: "flex",
             flexDirection: "column",
             justifyContent: "flex-start", // Garantir alinhamento no topo
           }}
         >
           {/* Nome da iniciativa */}
           <div
             style={{
               fontSize: levelStyles.fontSize,
               fontWeight: levelStyles.fontWeight,
               color: "#1e293b",
               lineHeight: "1.2",
               wordBreak: "break-word",
               overflow: "hidden",
               display: "-webkit-box",
               WebkitLineClamp: 2,
               WebkitBoxOrient: "vertical",
               height: `${parseFloat(levelStyles.fontSize) * 1.2 * 2 + 0.2}rem`, // Altura fixa para 2 linhas + espaço
               marginBottom: "8px", // Espaçamento fixo após o container
             }}
           >
             {data.name}
           </div>
           
           {/* Líderes da iniciativa */}
           {data.hierarchy_type === "initiative" && (data.leader || data.leader2) && (
             <div
               style={{
                 fontSize: `${parseFloat(levelStyles.fontSize) * 0.8}rem`,
                 fontWeight: "400",
                 color: "#64748b",
                 lineHeight: "1.1", // Reduzido para compactar
                 wordBreak: "break-word",
                 overflow: "hidden",
                 display: "-webkit-box",
                 WebkitLineClamp: 1,
                 WebkitBoxOrient: "vertical",
                 marginTop: "0px", // Garantir que não há margem superior
               }}
             >
               {[data.leader, data.leader2].filter(Boolean).join(", ")}
             </div>
           )}
         </div>
       )}

                                           {/* Indicador de status - diferenciado por tipo */}
         <div
           style={{
             display: "flex",
             alignItems: "center",
             gap: "4px",
             flexShrink: 0,
           }}
         >
           {/* Círculo de status - tamanho ainda maior para máxima evidência */}
           <div
             style={{
               width: data.hierarchy_type === "workfront" ? "36px" : "32px",
               height: data.hierarchy_type === "workfront" ? "36px" : "32px",
               borderRadius: "50%",
               backgroundColor: statusColor,
               boxShadow: data.hierarchy_type === "workfront" 
                 ? `0 0 0 6px ${statusColor}30` 
                 : `0 0 0 5px ${statusColor}20`,
               opacity: data.hierarchy_type === "workfront" ? 0.8 : 1,
             }}
           />
           
                     {/* Indicador de warning para initiatives */}
          {data.hierarchy_type === "initiative" && 
           userDetails?.has_status_warning !== false && (
            <StatusWarningIndicator
              initiativeId={data.id}
              currentStatus={data.status}
              size="14px"
              color="#ff9800"
              showTooltip={true}
              lazyLoad={false} // ✅ FORÇAR VALIDAÇÃO IMEDIATA NO WBS
              onLoadingStateChange={onValidationLoadingChange}
            />
          )}
         </div>

             {/* Indicador de seleção */}
       {selected && (
         <div style={{
           position: "absolute",
           top: "-2px",
           left: "-2px",
           right: "-2px",
           bottom: "-2px",
           borderRadius: "10px",
           border: `2px solid ${statusColor}`,
           pointerEvents: "none",
         }} />
       )}
    </div>
  );
};

// Componente de aresta customizado para garantir visibilidade
function CustomEdge({ id, sourceX, sourceY, targetX, targetY, data, clientColor = "#1e40af" }) {
  // Usar straight path para todas as arestas
  const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY });

  // Usar clientColor para iniciativas, cor mais suave para outros níveis
  const strokeColor = data?.hierarchy_type === "initiative" ? clientColor : "#94a3b8";
  const strokeWidth = data?.hierarchy_type === "initiative" ? 2 : 1.5;

  return (
    <BaseEdge 
      id={id} 
      path={edgePath}
      style={{
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeLinecap: "round",
        strokeDasharray: data?.hierarchy_type === "initiative" ? "0" : "6,4",
        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
      }}
      markerEnd={{
        type: "arrowclosed",
        width: data?.hierarchy_type === "initiative" ? 16 : 12,
        height: data?.hierarchy_type === "initiative" ? 16 : 12,
        color: strokeColor,
      }}
    />
  );
}

// Componente principal
// IMPORTANTE: Para que o Dialog de detalhes da iniciativa apareça corretamente no modo full screen,
// o componente pai deve configurar o Dialog com:
// - container: document.body (padrão)
// - zIndex: 10000 (maior que o fullScreenContainer que tem zIndex: 9999)
//
// CONFIGURAÇÃO DE INTERATIVIDADE:
// - WBS configurado como NÃO interativo por padrão
// - Usuários NÃO podem movimentar, conectar ou selecionar nós
// - Apenas navegação (pan/zoom) e clique em initiatives são permitidos
// - Controles de interatividade (cadeado) completamente desabilitados
export default function WBSVisualOverview({
  selectedSection,
  onInitiativeClick,
  clientColor,
  isLoading: externalIsLoading, // Loading externo para sincronização
  onFullScreenChange,
  onLoadingChange,
  onValidationLoadingChange,
}) {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const [deliverablesData, setDeliverablesData] = useState([]);
  // Loading interno sempre inicia true - loading externo é só para sincronizar exibição
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hierarchyConfig, setHierarchyConfig] = useState(["branch", "workfront", "initiative"]);
  const [loadingInitId, setLoadingInitId] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Estados do React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView, zoomIn, zoomOut } = useReactFlow();

  // Buscar configuração de hierarquias do cliente
  useEffect(() => {
    let isMounted = true;
    
    async function fetchHierarchyConfig() {
      try {
        const response = await client.get("/api/hierarchy-config/");
        
        if (isMounted && response.data.success) {
          // console.log("[WBSVisualOverview] Configuração de hierarquias:", response.data.data);
          setHierarchyConfig(response.data.data.hierarchy_levels);
        }
      } catch (err) {
        // console.error("Erro ao buscar configuração de hierarquias:", err);
        // Manter configuração padrão em caso de erro
      }
    }

    fetchHierarchyConfig();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Buscar dados de entregáveis
  useEffect(() => {
    let isMounted = true;
    
    async function fetchDeliverablesData() {
      try {
        setIsLoading(true);
        // Notificar o componente pai sobre início do loading
        if (onLoadingChange) {
          onLoadingChange(true);
        }
        setError(null);

        // console.log("[WBSVisualOverview] Buscando dados de entregáveis");

        const response = await client.post("/api/deliverables-overview-data/", {
          show_only_critical: false, // Sempre buscar todos para WBS
        });

        if (isMounted) {
          if (response.data.success) {
            // console.log("[WBSVisualOverview] Dados recebidos:", response.data.data?.length || 0, "itens");
            const data = response.data.data || [];
            setDeliverablesData(data);
            
            // IMPORTANTE: Garantir tempo para validações começarem
            // Adicionar uma validação "fantasma" que será removida após delay
            if (onValidationLoadingChange) {
              // Adicionar validação fantasma para manter loading ativo
              onValidationLoadingChange(true);
              
              // Remover a validação fantasma após delay para validações reais começarem
              setTimeout(() => {
                onValidationLoadingChange(false);
              }, 1500); // Delay suficiente para validações reais
            }
          } else {
            setError(response.data.error || "Erro ao buscar dados de entregáveis");
          }
        }
      } catch (err) {
        // console.error("Erro ao buscar dados de entregáveis:", err);
        if (isMounted) {
          setError("Erro de conexão com o servidor");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          // Notificar o componente pai sobre fim do loading
          if (onLoadingChange) {
            onLoadingChange(false);
          }
        }
      }
    }

    // Sempre buscar dados - loading externo é só para sincronizar exibição
    fetchDeliverablesData();
    
    return () => {
      isMounted = false;
    };
  }, []); // Busca apenas uma vez na inicialização

  // Construir nós e arestas para o React Flow
  const flowElements = useMemo(() => {
    if (!deliverablesData || deliverablesData.length === 0) {
      return { nodes: [], edges: [] };
    }

    const nodes = [];
    const edges = [];
    let nodeId = 0;

         // Agrupar dados por hierarquia
     const groupedData = {};
     
     // console.log("[HIERARCHY] Construindo hierarquia para", deliverablesData.length, "itens");
     // console.log("[HIERARCHY] Configuração:", hierarchyConfig);
     
     deliverablesData.forEach((item, index) => {
       const {
         branch = "Sem Branch",
         workfront = "Sem Workfront",
         initiative_name = "Sem Iniciativa",
         initiative_id,
         status = "gray",
         leader = "",
         leader2 = "",
       } = item;

       // Construir hierarquia baseada na configuração
       let currentLevel = groupedData;
       
       // Nível 1: Branch (se configurado)
       if (hierarchyConfig.includes("branch")) {
         if (!currentLevel[branch]) {
           currentLevel[branch] = {
             name: branch,
             status: "gray",
             level: 0,
             hierarchy_type: "branch",
             children: {},
           };
         }
         currentLevel = currentLevel[branch].children;
       }
       
       // Nível 2: Workfront (se configurado)
       if (hierarchyConfig.includes("workfront")) {
         if (!currentLevel[workfront]) {
           currentLevel[workfront] = {
             name: workfront,
             status: "gray",
             level: hierarchyConfig.includes("branch") ? 1 : 0,
             hierarchy_type: "workfront",
             children: {},
           };
         }
         currentLevel = currentLevel[workfront].children;
       }
       
               // Nível 3: Initiative (sempre presente)
        if (hierarchyConfig.includes("initiative")) {
          // IMPORTANTE: Initiatives devem ser nós independentes, não filhos dos workfronts
          // Elas serão conectadas via arestas, mas posicionadas verticalmente
          currentLevel[initiative_name] = {
            id: initiative_id,
            name: initiative_name,
            status: status.toLowerCase(),
            level: 1, // Sempre nível 1 para initiatives
            hierarchy_type: "initiative",
            parent_workfront: workfront, // Referência ao workfront pai
            parent_branch: branch, // Referência ao branch pai (se existir)
            leader: leader,
            leader2: leader2,
          };
        }
     });

     // console.log("[HIERARCHY] Estrutura construída:", JSON.stringify(groupedData, null, 2));
     
     // DEBUG ESPECÍFICO: Verificar se a hierarquia está sendo construída corretamente
     /*
     console.log("[HIERARCHY] Análise da estrutura:");
     Object.entries(groupedData).forEach(([branchKey, branchData]) => {
       console.log(`[HIERARCHY] Branch: ${branchKey}`);
       if (branchData.children) {
         Object.entries(branchData.children).forEach(([workfrontKey, workfrontData]) => {
           console.log(`[HIERARCHY]   └─ Workfront: ${workfrontKey}`);
           if (workfrontData.children) {
             Object.entries(workfrontData.children).forEach(([initiativeKey, initiativeData]) => {
               console.log(`[HIERARCHY]     └─ Initiative: ${initiativeKey} (ID: ${initiativeData.id})`);
             });
           }
         });
       }
     });
     */

    // Função para agregar status (mesma lógica do DeliverablesOverviewTable)
    const aggregateStatus = (statusArray) => {
      if (statusArray.includes("red")) return "red";
      if (statusArray.includes("yellow")) return "yellow";
      if (statusArray.includes("green")) return "green";
      return "gray";
    };

    // Função recursiva para calcular agregações de status
    const calculateStatusAggregations = (levelData) => {
      Object.values(levelData).forEach((item) => {
        if (item.children && Object.keys(item.children).length > 0) {
          // Calcular agregações dos filhos primeiro
          calculateStatusAggregations(item.children);
          
          // Agregar status dos filhos
          const childItems = Object.values(item.children);
          const childStatuses = childItems.map((child) => child.status);
          item.status = aggregateStatus(childStatuses);
        }
      });
    };

    // Calcular agregações de status
    calculateStatusAggregations(groupedData);

                             // Função para criar nós e arestas com hierarquia corrigida
        const createNodesAndEdges = (data) => {
          // console.log(`[NODES] Criando nós com hierarquia corrigida`);
          
          // Primeiro, criar todos os workfronts (nível 0)
          Object.entries(data).forEach(([workfrontKey, workfrontData]) => {
            const workfrontNodeId = `node-${nodeId++}`;
            
            /*
            console.log(`[NODES] Criando workfront ${workfrontNodeId}:`, { 
              name: workfrontData.name, 
              type: workfrontData.hierarchy_type, 
              level: 0
            });
            */
            
            // Criar nó do workfront
            nodes.push({
              id: workfrontNodeId,
              type: "customNode",
              data: {
                ...workfrontData,
                level: 0,
                hierarchy_type: "workfront",
              },
              position: { x: 0, y: 0 },
            });

            // Depois, criar todas as initiatives deste workfront (nível 1)
            if (workfrontData.children) {
              Object.entries(workfrontData.children).forEach(([initiativeKey, initiativeData]) => {
                const initiativeNodeId = `node-${nodeId++}`;
                
                /*
                console.log(`[NODES] Criando initiative ${initiativeNodeId}:`, { 
                  name: initiativeData.name, 
                  type: initiativeData.hierarchy_type, 
                  level: 1,
                  parentWorkfront: workfrontNodeId
                });
                */
                
                // Criar nó da initiative
                nodes.push({
                  id: initiativeNodeId,
                  type: "customNode",
                  data: {
                    ...initiativeData,
                    level: 1,
                    hierarchy_type: "initiative",
                  },
                  position: { x: 0, y: 0 },
                });

                // Criar aresta conectando workfront à initiative
                // console.log(`[EDGES] Criando aresta: ${workfrontNodeId} -> ${initiativeNodeId} (workfront->initiative)`);
                edges.push({
                  id: `edge-${workfrontNodeId}-${initiativeNodeId}`,
                  source: workfrontNodeId,
                  target: initiativeNodeId,
                  type: "customEdge",
                  data: {
                    hierarchy_type: "initiative",
                    sourceLevel: 0,
                    targetLevel: 1,
                  },
                  style: {
                    stroke: "#1e40af",
                    strokeWidth: 3,
                    strokeDasharray: "0",
                  },
                  markerEnd: {
                    type: "arrowclosed",
                    width: 18,
                    height: 18,
                    color: "#1e40af",
                  },
                });
              });
            }
          });
        };

         createNodesAndEdges(groupedData);

     // console.log("[FLOW] Resumo da construção:");
     // console.log("[FLOW] - Nós criados:", nodes.length);
     // console.log("[FLOW] - Arestas criadas:", edges.length);
     // console.log("[FLOW] - Estrutura hierárquica:", nodes.map(n => ({ 
     //   id: n.id, 
     //   name: n.data.name, 
     //   type: n.data.hierarchy_type, 
     //   level: n.data.level,
     //   parent: edges.find(e => e.target === n.id)?.source || 'raiz'
     // })));

    return { nodes, edges };
  }, [deliverablesData, hierarchyConfig]);

           // Aplicar layout quando os elementos mudarem
    useEffect(() => {
      if (flowElements.nodes.length > 0) {
        // console.log("[LAYOUT] Aplicando layout para", flowElements.nodes.length, "nós e", flowElements.edges.length, "arestas");
        
        // Usar dagre para calcular posições automaticamente
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
          flowElements.nodes,
          flowElements.edges,
          'TB' // Top to Bottom - garante distribuição vertical
        );
        
        // console.log("[LAYOUT] Layout calculado, aplicando ao React Flow...");
        
        // Validar arestas antes de aplicar
        let finalEdges = layoutedEdges;
        
        // Verificar se há arestas válidas
        if (layoutedEdges.length === 0 && layoutedNodes.length > 1) {
          // console.warn("[LAYOUT] Nenhuma aresta encontrada, criando aresta de teste");
          finalEdges = [{
            id: 'test-edge',
            source: layoutedNodes[0].id,
            target: layoutedNodes[1].id,
            type: 'customEdge',
            data: { hierarchy_type: 'test' },
            style: { stroke: '#ff0000', strokeWidth: 3 }
          }];
        }
        
        // Validar integridade das arestas
        const nodeIds = layoutedNodes.map(n => n.id);
        const validEdges = finalEdges.filter(e => 
          e.source && e.target && 
          nodeIds.includes(e.source) && 
          nodeIds.includes(e.target)
        );
        
        if (validEdges.length !== finalEdges.length) {
          /*
          console.warn("[LAYOUT] Arestas inválidas removidas:", 
            finalEdges.length - validEdges.length, "de", finalEdges.length);
            */
        }
        
        // console.log("[LAYOUT] Aplicando ao React Flow:", layoutedNodes.length, "nós e", validEdges.length, "arestas válidas");
        
        setNodes(layoutedNodes);
        setEdges(validEdges);
        
                 // Ajustar view após um pequeno delay para garantir que o layout foi aplicado
         setTimeout(() => {
           // console.log("[LAYOUT] Ajustando view com fitView otimizado...");
           
                                   // Calcular a largura total do diagrama para otimizar o zoom
            const allNodes = layoutedNodes;
            if (allNodes.length > 0) {
              const workfrontCount = allNodes.filter(n => n.data.hierarchy_type === 'workfront').length;
              const sizes = calculateProportionalSizes(workfrontCount);
              
              const xPositions = allNodes.map(n => n.position.x);
              const minX = Math.min(...xPositions);
              const maxX = Math.max(...xPositions);
              const totalWidth = maxX - minX + sizes.NODE_WIDTH;
              
              /*
              console.log("[LAYOUT] Dimensões calculadas:", {
                minX,
                maxX,
                totalWidth,
                nodeWidth: sizes.NODE_WIDTH,
                nodesCount: allNodes.length,
                workfrontCount,
                proportionalSizes: sizes
              });
              */
              
                            // Aplicar fitView com padding proporcional
               const padding = workfrontCount <= 4 ? 0.1 : workfrontCount <= 8 ? 0.05 : 0.02;
               
               fitView({ 
                 padding: padding, // Padding proporcional baseado no número de workfronts
                 duration: 1000,
                 includeHiddenNodes: false,
                 minZoom: 0.3,
                 maxZoom: 2
               });
                         } else {
                // Fallback para fitView padrão
                const fallbackPadding = 0.05; // Padding padrão para fallback
                
                fitView({ 
                  padding: fallbackPadding, // Padding padrão
                  duration: 1000,
                  includeHiddenNodes: false,
                  minZoom: 0.3,
                  maxZoom: 2
                });
            }
         }, 150);
      }
    }, [flowElements, setNodes, setEdges, fitView]);

  // Handler para clique em nó
  const onNodeClick = useCallback(async (event, node) => {
    if (node.data.hierarchy_type === "initiative" && node.data.id) {
      setLoadingInitId(node.data.id);
      try {
        await onInitiativeClick(node.data.id);
      } finally {
        setLoadingInitId(null);
      }
    }
  }, [onInitiativeClick]);

  // Handler para conexões (não usado neste contexto)
  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  // Handler para alternar full screen
  const toggleFullScreen = useCallback(() => {
    // console.log("[FULLSCREEN] Alternando full screen");
    setIsFullScreen(prev => {
      const newState = !prev;
      // console.log("[FULLSCREEN] Novo estado:", newState);
      return newState;
    });
  }, []);

  // Notificar mudanças no modo full screen para o componente pai
  useEffect(() => {
    if (onFullScreenChange) {
      onFullScreenChange(isFullScreen);
    }
  }, [isFullScreen, onFullScreenChange]);

  // Usar loading externo para sincronização se fornecido, caso contrário usar interno
  const shouldShowLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  // Estilos
  const useStyles = makeStyles((theme) => ({
         container: {
       height: "100%",
       width: "100%",
       display: "flex",
       flexDirection: "column",
       backgroundColor: "#fafbfc",
       borderRadius: "12px",
       overflow: "hidden",
       border: "1px solid #e2e8f0",
       transition: "all 0.3s ease",
     },
                       fullScreenContainer: {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              backgroundColor: "#fafbfc",
              borderRadius: 0,
              border: "none",
              width: "100vw",
              height: "100vh",
              overflow: "hidden",
              // Garantir que esteja acima de outros elementos mas abaixo de modais
              isolation: "isolate",
            },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 20px",
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #e2e8f0",
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
    title: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#1e293b",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    controls: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    controlButton: {
      padding: "6px",
      borderRadius: "6px",
      color: "#64748b",
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: "#f1f5f9",
        color: "#334155",
        transform: "scale(1.05)",
      },
    },
         flowContainer: {
       flex: 1,
       position: "relative",
       backgroundColor: "#f8fafc",
       width: "100%",
       height: "100%",
     },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "400px",
      padding: "20px",
    },
    errorContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "40px 20px",
      textAlign: "center",
    },
    errorIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      backgroundColor: "#fef2f2",
      border: "2px solid #fecaca",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
    },
    errorTitle: {
      fontSize: "1rem",
      color: "#ef4444",
      fontWeight: "600",
      marginBottom: "8px",
    },
    errorMessage: {
      fontSize: "0.875rem",
      color: "#64748b",
      maxWidth: "400px",
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "400px",
      padding: "20px",
      textAlign: "center",
    },
    emptyIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      backgroundColor: "#f1f5f9",
      border: "2px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
      color: "#94a3b8",
    },
    emptyTitle: {
      fontSize: "1rem",
      color: "#475569",
      fontWeight: "600",
      marginBottom: "8px",
    },
    emptyMessage: {
      fontSize: "0.875rem",
      color: "#64748b",
      maxWidth: "300px",
    },
  }));

  const classes = useStyles();

     // Renderizar loading
   if (shouldShowLoading) {
     return (
       <div className={isFullScreen ? classes.fullScreenContainer : classes.container}>
         <div className={classes.loadingContainer}>
           <ThreeDots
             color={clientColor}
             height={80}
             width={80}
             ariaLabel="loading"
           />
         </div>
       </div>
     );
   }

     // Renderizar erro
   if (error) {
     return (
       <div className={isFullScreen ? classes.fullScreenContainer : classes.container}>
         <div className={classes.errorContainer}>
           <div className={classes.errorIcon}>
             <FontAwesomeIcon
               icon={faExclamationTriangle}
               style={{ fontSize: "20px", color: "#ef4444" }}
             />
           </div>
           <Typography className={classes.errorTitle}>
             Erro ao carregar dados
           </Typography>
           <Typography className={classes.errorMessage}>
             {error}
           </Typography>
         </div>
       </div>
     );
   }

     // Renderizar estado vazio
   if (nodes.length === 0) {
     return (
       <div className={isFullScreen ? classes.fullScreenContainer : classes.container}>
         <div className={classes.emptyState}>
           <div className={classes.emptyIcon}>
             <FontAwesomeIcon
               icon={faExclamationTriangle}
               style={{ fontSize: "24px" }}
             />
           </div>
           <Typography className={classes.emptyTitle}>
             Nenhum dado disponível
           </Typography>
           <Typography className={classes.emptyMessage}>
             Não há dados de entregáveis para exibir no momento.
           </Typography>
         </div>
       </div>
     );
   }

           // console.log("[RENDER] Estado atual:", { isFullScreen, containerClass: isFullScreen ? 'fullScreenContainer' : 'container' });
      
      return (
      <div className={isFullScreen ? classes.fullScreenContainer : classes.container}>
        {/* Container do React Flow */}
        <div className={classes.flowContainer}>
                                   <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
                                                                nodeTypes={{ 
                 customNode: (props) => {
                   // Calcular tamanhos proporcionais baseado no número de workfronts
                   const workfrontCount = nodes.filter(n => n.data.hierarchy_type === 'workfront').length;
                   const proportionalSizes = calculateProportionalSizes(workfrontCount);
                   
                   return (
                                           <CustomNode 
                        {...props} 
                        clientColor={clientColor}
                        isLoading={loadingInitId === props.data.id}
                        t={t}
                        proportionalSizes={proportionalSizes}
                        onValidationLoadingChange={onValidationLoadingChange}
                        userDetails={userDetails}
                      />
                   );
                 }
               }}
            edgeTypes={{ 
              customEdge: (props) => <CustomEdge {...props} clientColor={clientColor} />
            }}
            fitView
            attributionPosition="bottom-left"
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            minZoom={0.3}
            maxZoom={2}
                                                                             fitViewOptions={{
                 padding: nodes.length > 0 ? 
                   (() => {
                     const workfrontCount = nodes.filter(n => n.data.hierarchy_type === 'workfront').length;
                     return workfrontCount <= 4 ? 0.1 : workfrontCount <= 8 ? 0.05 : 0.02;
                   })() : 0.05, // Padding proporcional baseado no número de workfronts
                 includeHiddenNodes: false,
                 minZoom: 0.3,
                 maxZoom: 2
               }}
            style={{
              backgroundColor: "#f8fafc",
              width: "100%",
              height: "100%",
            }}
            proOptions={{ hideAttribution: true }}
            elevateEdgesOnSelect={true}
            elevateNodesOnSelect={true}
            onError={(error) => {} } // console.error("[ReactFlow] Erro:", error)}
            // WBS não interativo por padrão - usuários não podem movimentar nós
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={false}
          >
                     <Background
             color="#e2e8f0"
             gap={20}
             size={1}
             style={{ opacity: 0.3 }}
           />
           
           
                                             {/* Controles personalizados do ReactFlow - lado esquerdo */}
            <div
              style={{
                position: "absolute",
                bottom: "20px",
                left: "20px",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "4px",
                zIndex: 1000,
              }}
            >
              {/* Zoom In */}
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={() => zoomIn({ duration: 300 })}
                  style={{ padding: "6px" }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {/* Zoom Out */}
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={() => zoomOut({ duration: 300 })}
                  style={{ padding: "6px" }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
                             {/* Fit View - com ícone diferente do Full Screen */}
                                               <Tooltip title="Ajustar View">
                   <IconButton
                     size="small"
                     onClick={() => {
                       const workfrontCount = nodes.filter(n => n.data.hierarchy_type === 'workfront').length;
                       const sizes = calculateProportionalSizes(workfrontCount);
                       
                       fitView({
                         padding: workfrontCount <= 4 ? 0.1 : workfrontCount <= 8 ? 0.05 : 0.02, // Padding proporcional
                         duration: 1000,
                         includeHiddenNodes: false,
                         minZoom: 0.3,
                         maxZoom: 2
                       });
                     }}
                     style={{ padding: "6px" }}
                   >
                     <FitViewIcon fontSize="small" />
                   </IconButton>
                 </Tooltip>
              
              {/* Separador visual */}
              <div style={{ 
                height: "1px", 
                backgroundColor: "#e2e8f0", 
                margin: "2px 4px" 
              }} />
              
              {/* Full Screen - agrupado com outros controles */}
              <Tooltip title={isFullScreen ? "Sair do Full Screen" : "Full Screen"}>
                <IconButton
                  size="small"
                  onClick={toggleFullScreen}
                  style={{ padding: "6px" }}
                >
                  {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullScreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </div>
            
            
                     <MiniMap
             style={{
               backgroundColor: "#ffffff",
               border: "1px solid #e2e8f0",
               borderRadius: "8px",
             }}
             nodeColor={(node) => {
               const status = node.data?.status;
               switch (status) {
                 case "green": return "#10b981";
                 case "yellow": return "#f59e0b";
                 case "red": return "#ef4444";
                 default: return "#9ca3af";
               }
             }}
             nodeStrokeWidth={2}
             // MiniMap não interativo - apenas visualização
             zoomable={false}
             pannable={false}
           />
        </ReactFlow>
      </div>
    </div>
     
   );
 }
